import React, { useState } from 'react';
import '../styles/PhoneSimulator.css';

interface DeviceProfile {
  label: string;
  width: number;
  height: number;
  devicePixelRatio: string;
  emoji: string;
}

const DEVICES: DeviceProfile[] = [
  { label: 'iPhone 14',     width: 390, height: 844, devicePixelRatio: '3x', emoji: '📱' },
  { label: 'iPhone SE',     width: 375, height: 667, devicePixelRatio: '2x', emoji: '📱' },
  { label: 'Samsung S23',   width: 360, height: 780, devicePixelRatio: '3x', emoji: '📱' },
  { label: 'Pixel 7',       width: 412, height: 915, devicePixelRatio: '2.6x', emoji: '📱' },
  { label: 'Tablet 768',    width: 768, height: 1024, devicePixelRatio: '2x', emoji: '📟' },
];

const SITE_URL = `${window.location.origin}/login`;

export function PhoneSimulator(): JSX.Element {
  const [deviceIdx, setDeviceIdx] = useState(0);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [iframeKey, setIframeKey] = useState(0);

  const device = DEVICES[deviceIdx];
  const frameW = orientation === 'portrait' ? device.width  : device.height;
  const frameH = orientation === 'portrait' ? device.height : device.width;

  function reload(): void {
    setIframeKey(k => k + 1);
  }

  return (
    <div className="sim-page">
      {/* ── Top toolbar ──────────────────────────────────── */}
      <div className="sim-toolbar">
        <div className="sim-toolbar__left">
          <span className="sim-logo">📱 סימולטור סלולר</span>
        </div>

        <div className="sim-toolbar__controls">
          {/* Device picker */}
          <div className="sim-control-group">
            <label className="sim-label">מכשיר</label>
            <div className="sim-device-pills">
              {DEVICES.map((d, i) => (
                <button
                  key={d.label}
                  className={`sim-pill${deviceIdx === i ? ' sim-pill--active' : ''}`}
                  onClick={() => setDeviceIdx(i)}
                  title={`${d.width}×${d.height}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Orientation */}
          <div className="sim-control-group">
            <label className="sim-label">כיוון</label>
            <div className="sim-orient-toggle">
              <button
                className={`sim-orient-btn${orientation === 'portrait' ? ' sim-orient-btn--active' : ''}`}
                onClick={() => setOrientation('portrait')}
                title="Portrait"
              >⬜</button>
              <button
                className={`sim-orient-btn${orientation === 'landscape' ? ' sim-orient-btn--active' : ''}`}
                onClick={() => setOrientation('landscape')}
                title="Landscape"
              >⬛</button>
            </div>
          </div>

          {/* Reload */}
          <button className="sim-reload-btn" onClick={reload} title="רענן">
            ↻
          </button>
        </div>

        <div className="sim-toolbar__info">
          <span className="sim-dim-badge">{frameW} × {frameH}</span>
          <span className="sim-dpr-badge">{device.devicePixelRatio}</span>
        </div>
      </div>

      {/* ── Phone frame area ─────────────────────────────── */}
      <div className="sim-stage">
        <div
          className={`sim-phone${orientation === 'landscape' ? ' sim-phone--landscape' : ''}`}
          style={{ '--phone-w': `${frameW}px`, '--phone-h': `${frameH}px` } as React.CSSProperties}
        >
          {/* Phone shell */}
          <div className="sim-phone__shell">
            {/* Notch / dynamic island */}
            <div className="sim-notch">
              <div className="sim-notch__island" />
            </div>

            {/* Side buttons */}
            <div className="sim-btn sim-btn--vol-up"   aria-hidden="true" />
            <div className="sim-btn sim-btn--vol-down" aria-hidden="true" />
            <div className="sim-btn sim-btn--power"    aria-hidden="true" />

            {/* Screen bezel */}
            <div className="sim-screen">
              {/* Status bar */}
              <div className="sim-status-bar">
                <span className="sim-status-time">9:41</span>
                <span className="sim-status-icons">
                  <span>▐▐▐</span>
                  <span>WiFi</span>
                  <span>🔋</span>
                </span>
              </div>

              {/* The actual website */}
              <iframe
                key={iframeKey}
                src={SITE_URL}
                title="Mobile Preview"
                className="sim-iframe"
                style={{ width: frameW, height: frameH }}
                allow="same-origin"
              />

              {/* Home indicator */}
              <div className="sim-home-bar" aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* Device label below phone */}
        <div className="sim-device-label">
          {device.emoji} {device.label} — {frameW}×{frameH}px
        </div>
      </div>
    </div>
  );
}
